/**
 * The stolen uniform (02 §6.4, generalised from missions 9 and 12). Knife a man at a building
 * somebody lives or works in and come away dressed as him: infantry look straight through you
 * until you fire - though officers and elites close by look twice.
 *
 * Buildings are solid in this sim, so "inside with him" reads as "at its wall".
 */

import { type Ctx, isFighter } from '../ctx';
import { isAutomated, isDog } from '../enemies/specials/kinds';
import { PROPS } from '../props';
import { clearSight } from '../smoke';
import { OBJECTIVES } from '../tuning';
import type { Enemy, EnemyKind, PropKind, World } from '../types';
import { dist, propDestroyed, propDistance } from './common';
import type { PlayerNoise } from './noise';

/**
 * Where a man can change: every prop the data calls `shelter` (`PropSpec.shelter`). This was a
 * hand-written list of three kinds, and the HUD kept a second copy of it - so a body dragged to a
 * barracks, the largest building on the map, did nothing whatever and said nothing about why.
 * One derivation now, read by both.
 */
export const INTERIORS: ReadonlyArray<PropKind> =
  (Object.keys(PROPS) as PropKind[]).filter((kind) => PROPS[kind].shelter === true);

/**
 * Whose uniform is worth wearing. Motion detectors do not read insignia: the automated kinds ignore disguises.
 * ROUND8: nor does a dog wear one - knifing it at a barracks dresses nobody - and a dog reports no sighting to anyone.
 */
export const wearsUniform = (kind: EnemyKind): boolean => !isAutomated(kind) && !isDog(kind);

/**
 * Called from perception: true when this soldier sees only another soldier. A man already
 * fighting the ranger watched him change, and is not fooled. ROUND8: a dog goes by his scent, which no coat changes.
 */
export function disguiseHides(w: World, e: Pick<Enemy, 'kind' | 'state'>): boolean {
  return w.objective.disguised && !isAutomated(e.kind) && !isDog(e.kind) && e.state !== 'combat';
}

/** Is this spot at the wall of a standing building somebody lives or works in? */
export function nearInterior(ctx: Pick<Ctx, 'world' | 'statics'>, x: number, z: number): boolean {
  for (const prop of ctx.statics.def.props) {
    if (!INTERIORS.includes(prop.kind) || propDestroyed(ctx.world, prop.id)) continue;
    if (propDistance(prop, x, z) <= OBJECTIVES.interiorReach) return true;
  }
  return false;
}

export function setDisguise(ctx: Ctx, on: boolean): void {
  const o = ctx.world.objective;
  if (o.disguised === on) return;
  o.disguised = on;
  o.scrutinyTicks = 0;
  ctx.events.push({ type: 'disguise', on });
}

/** The man the knife killed this tick, if any: `stab` reports the casualty just before the `knife` event. */
function knifeVictim(ctx: Ctx): Enemy | null {
  let lastKilled = -1;
  for (const ev of ctx.events) {
    if (ev.type === 'enemyHit' && ev.killed) lastKilled = ev.id;
    else if (ev.type === 'knife') {
      if (!ev.hit || lastKilled < 0) return null;
      return ctx.world.enemies.find((e) => e.id === lastKilled) ?? null;
    }
  }
  return null;
}

export function stepDisguise(ctx: Ctx, noise: PlayerNoise): void {
  const w = ctx.world;
  const o = w.objective;
  const r = w.ranger;

  if (o.disguised && noise.fired) setDisguise(ctx, false);

  if (!o.disguised) {
    const victim = knifeVictim(ctx);
    if (victim && wearsUniform(victim.kind) && nearInterior(ctx, r.x, r.z)) setDisguise(ctx, true);
    return;
  }

  // Officers and elites within a few metres look twice; hurrying or crawling past them looks wrong.
  let watcher: Enemy | null = null;
  for (const e of w.enemies) {
    if ((e.kind !== 'officer' && e.kind !== 'elite') || !isFighter(e) || e.state === 'down') continue;
    if (dist(e.x, e.z, r.x, r.z) > OBJECTIVES.scrutinyRadius) continue;
    if (!clearSight(ctx, e.x, e.z, 1.6, r.x, r.z, r.stance === 'crawl' ? 0.4 : 1.5)) continue;
    watcher = e;
    break;
  }
  if (!watcher) {
    o.scrutinyTicks = Math.max(0, o.scrutinyTicks - OBJECTIVES.scrutinyDecay);
    return;
  }
  const haste = r.running || r.stance === 'crawl' ? OBJECTIVES.scrutinyHasteFactor : 1;
  o.scrutinyTicks += haste * w.mods.disguiseScrutiny;
  if (o.scrutinyTicks >= OBJECTIVES.scrutinyTicks) {
    setDisguise(ctx, false);
    // He has made up his mind: his next look is a full detection.
    watcher.detection = 1;
    watcher.targetX = r.x;
    watcher.targetZ = r.z;
  }
}
